import {getDefaultRouteForRole, ROUTE_PATHS, ROUTE_TITLES} from '@/router/routePaths.ts';
import {AuthenticatedOutletContext} from '@/router/useAuthenticatedContext.ts';
import {Dock} from '@/shared/components/layout/Dock.tsx';
import {Header} from '@/shared/components/layout/Header.tsx';
import {ActionDockItem} from '@/shared/components/ui/ActionDock.tsx';
import {FloatingActionButton} from '@/shared/components/ui/FloatingActionButton.tsx';
import {LoadingScreen} from '@/shared/components/ui/loading/LoadingScreen.tsx';
import {useAuth} from '@/shared/contexts/AuthContext.tsx';
import {UserRole} from '@/shared/types/common.ts';
import {ServiceWithNames} from '@/shared/types/service.ts';
import {ArrowLeftRight, Receipt, Scissors, UserPlus, Wallet} from 'lucide-react';
import {lazy, Suspense, useCallback, useMemo, useState} from 'react';
import {Navigate, Outlet, useLocation} from 'react-router';

// Lazy load sheets - only loaded when user opens them
const AddExpenseSheet = lazy(() =>
    import('@/shared/components/ui/sheets/AddExpenseSheet.tsx').then((m) => ({
        default: m.AddExpenseSheet,
    }))
);
const AddTransferSheet = lazy(() =>
    import('@/shared/components/ui/sheets/AddTransferSheet.tsx').then((m) => ({
        default: m.AddTransferSheet,
    }))
);
const ClientCreateSheet = lazy(() =>
    import('@/shared/components/ui/sheets/ClientCreateSheet.tsx').then((m) => ({
        default: m.ClientCreateSheet,
    }))
);
const PayEmployeeSheet = lazy(() =>
    import('@/shared/components/ui/sheets/PayEmployeeSheet.tsx').then((m) => ({
        default: m.PayEmployeeSheet,
    }))
);
const RegisterServiceSheet = lazy(() =>
    import('@/shared/components/ui/sheets/RegisterServiceSheet.tsx').then((m) => ({
        default: m.RegisterServiceSheet,
    }))
);

type SheetType = 'service' | 'client' | 'expense' | 'transfer' | 'pay-employee' | null;

type AuthenticatedLayoutProps = {
    allowedRole: UserRole;
};

const ACTION_IDS = {
    SERVICE: 'service',
    CLIENT: 'client',
    EXPENSE: 'expense',
    TRANSFER: 'transfer',
    PAY_EMPLOYEE: 'pay-employee',
} as const;

const ACTION_LABELS = {
    SERVICE: 'Registrar Serviço',
    CLIENT: 'Novo Cliente',
    EXPENSE: 'Nova Despesa/Entrada',
    TRANSFER: 'Transferência',
    PAY_EMPLOYEE: 'Pagar Profissional',
} as const;

const ACTION_COLORS = {
    SERVICE: undefined, // Uses default accent
    CLIENT: 'var(--success)', // Success green
    EXPENSE: 'var(--error)', // Error red
    TRANSFER: 'var(--info)', // Info blue
    PAY_EMPLOYEE: 'var(--earning-employee)', // Purple
} as const;

/**
 * Layout component for authenticated users.
 * Handles role-based access control and provides common UI elements.
 *
 * Note: Authentication is already verified by the route loader (requireAuth).
 * This component only needs to:
 * 1. Wait for AuthContext to load employee/role data from Supabase
 * 2. Verify role matches the allowed role for this route
 * 3. Render the layout with header, content, and dock
 */
export const AuthenticatedLayout = ({allowedRole}: AuthenticatedLayoutProps) => {
    const {loading, role, employee, hasAccess} = useAuth();
    const location = useLocation();
    const [activeSheet, setActiveSheet] = useState<SheetType>(null);
    const [editService, setEditService] = useState<ServiceWithNames | null>(null);

    const handleOpenSheet = useCallback((sheet: SheetType) => {
        setActiveSheet(sheet);
        setEditService(null); // Clear edit state when opening fresh
    }, []);

    const handleCloseSheet = useCallback(() => {
        setActiveSheet(null);
        setEditService(null);
    }, []);

    const handleEditService = useCallback((service: ServiceWithNames) => {
        setEditService(service);
        setActiveSheet('service');
    }, []);

    const title = useMemo(() => {
        return ROUTE_TITLES[location.pathname] || 'Conquest';
    }, [location.pathname]);

    const employeeId = useMemo(() => {
        return employee?.id || '';
    }, [employee?.id]);

    const employeeName = useMemo(() => {
        return employee?.name || '';
    }, [employee?.name]);

    const employeeDepartment = useMemo(() => {
        return employee?.department || null;
    }, [employee?.department]);

    const outletContext = useMemo<AuthenticatedOutletContext>(() => {
        return {employeeId, employeeName, employeeDepartment, onEditService: handleEditService};
    }, [employeeId, employeeName, employeeDepartment, handleEditService]);

    // Build role-based actions for the FAB
    const fabActions = useMemo<ActionDockItem[] | undefined>(() => {
        // Employee: direct action, no menu
        if (allowedRole === 'employee') {
            return undefined;
        }

        // Receptionist: 2 actions
        if (allowedRole === 'receptionist') {
            return [
                {
                    id: ACTION_IDS.SERVICE,
                    label: ACTION_LABELS.SERVICE,
                    icon: Scissors,
                    onClick: () => handleOpenSheet('service'),
                    color: ACTION_COLORS.SERVICE,
                },
                {
                    id: ACTION_IDS.CLIENT,
                    label: ACTION_LABELS.CLIENT,
                    icon: UserPlus,
                    onClick: () => handleOpenSheet('client'),
                    color: ACTION_COLORS.CLIENT,
                },
            ];
        }

        // Owner: 5 actions
        if (allowedRole === 'owner') {
            return [
                {
                    id: ACTION_IDS.SERVICE,
                    label: ACTION_LABELS.SERVICE,
                    icon: Scissors,
                    onClick: () => handleOpenSheet('service'),
                    color: ACTION_COLORS.SERVICE,
                },
                {
                    id: ACTION_IDS.CLIENT,
                    label: ACTION_LABELS.CLIENT,
                    icon: UserPlus,
                    onClick: () => handleOpenSheet('client'),
                    color: ACTION_COLORS.CLIENT,
                },
                {
                    id: ACTION_IDS.EXPENSE,
                    label: ACTION_LABELS.EXPENSE,
                    icon: Receipt,
                    onClick: () => handleOpenSheet('expense'),
                    color: ACTION_COLORS.EXPENSE,
                },
                {
                    id: ACTION_IDS.TRANSFER,
                    label: ACTION_LABELS.TRANSFER,
                    icon: ArrowLeftRight,
                    onClick: () => handleOpenSheet('transfer'),
                    color: ACTION_COLORS.TRANSFER,
                },
                {
                    id: ACTION_IDS.PAY_EMPLOYEE,
                    label: ACTION_LABELS.PAY_EMPLOYEE,
                    icon: Wallet,
                    onClick: () => handleOpenSheet('pay-employee'),
                    color: ACTION_COLORS.PAY_EMPLOYEE,
                },
            ];
        }

        return undefined;
    }, [allowedRole, handleOpenSheet]);

    // Direct click handler for employee role (no menu)
    const handleFabClick = useCallback(() => {
        if (allowedRole === 'employee') {
            handleOpenSheet('service');
        }
    }, [allowedRole, handleOpenSheet]);

    // Show loading while AuthContext loads employee data from Supabase
    if (loading) {
        return <LoadingScreen message="Carregando..." />;
    }

    // User doesn't have access (not in employees table)
    if (!hasAccess) {
        return <Navigate to={ROUTE_PATHS.LOGIN} replace />;
    }

    // Redirect to correct role-based route if user doesn't have access to this section
    if (role !== allowedRole) {
        return <Navigate to={getDefaultRouteForRole(role!)} replace />;
    }

    return (
        <div className="app-container">
            <a href="#main-content" className="skip-to-content">
                Pular para o conteúdo principal
            </a>
            <Header title={title} />

            <main id="main-content" className="main-content" tabIndex={-1}>
                <Outlet context={outletContext} />
            </main>

            <FloatingActionButton onClick={handleFabClick} actions={fabActions} />

            {/* Lazy-loaded sheets - only loaded when opened */}
            <Suspense fallback={null}>
                {activeSheet === 'service' && (
                    <RegisterServiceSheet
                        isOpen={true}
                        onClose={handleCloseSheet}
                        currentUserName={employeeName}
                        currentUserRole={allowedRole}
                        editService={editService}
                    />
                )}

                {activeSheet === 'client' && (
                    <ClientCreateSheet
                        isOpen={true}
                        onClose={handleCloseSheet}
                        onCreated={handleCloseSheet}
                        actorId={employeeId}
                    />
                )}

                {activeSheet === 'expense' && (
                    <AddExpenseSheet
                        isOpen={true}
                        onClose={handleCloseSheet}
                        onSuccess={handleCloseSheet}
                    />
                )}

                {activeSheet === 'transfer' && (
                    <AddTransferSheet
                        isOpen={true}
                        onClose={handleCloseSheet}
                        onSuccess={handleCloseSheet}
                    />
                )}

                {activeSheet === 'pay-employee' && (
                    <PayEmployeeSheet
                        isOpen={true}
                        onClose={handleCloseSheet}
                        onSuccess={handleCloseSheet}
                    />
                )}
            </Suspense>

            <Dock role={allowedRole} />
        </div>
    );
};
